/**
 * AI Product Advisor — Floating chat widget for The Electrical Outlets
 * Replaces Shopify Inbox with AI-powered product recommendations,
 * BS 7671 compliance advice, and cross-sell suggestions.
 * v1.0.1 — 2026-04-19 (fix: self -> this in callAI/formatMessage)
 */
(function() {
  'use strict';

  const STORAGE_KEY = 'kt_advisor_history';
  const MAX_HISTORY = 20;
  const API_TIMEOUT = 30000;

  // Build system prompt dynamically from store config
  function buildSystemPrompt(config) {
    return [
      'You are ' + config.storeName + ' AI Product Advisor — a knowledgeable electrical supplies assistant for a UK-based e-commerce store.',
      'Your expertise covers: switches & sockets (BG, Schneider, Hamilton, MK), consumer units, MCBs, RCBOs, cable, fire safety, emergency lighting, hospitality/hotel supplies, smart home products.',
      '',
      'RULES:',
      '- Always consider BS 7671 (18th Edition) wiring regulations when recommending products',
      '- For bathroom queries: always mention IP ratings (IP44 minimum for Zone 2, IP65+ for Zone 1)',
      '- For outdoor queries: recommend IP65+ rated products',
      '- When comparing products: include key specs, price range, and compliance notes',
      '- For bulk/trade queries: mention our trade account with up to 40% discount',
      '- Keep responses concise (2-4 sentences max unless the question requires detail)',
      '- Use British English spellings',
      '- If asked about installation: recommend a qualified electrician for Part P notifiable work',
      '- Suggest related products when relevant (cross-sell)',
      '- Format product names in bold when mentioning specific items',
      '',
      'STORE CONTEXT:',
      '- ' + config.storeName + ' (' + config.domain + ') — UK electrical wholesaler & retailer',
      '- Sister stores: ' + (config.sisterStores || 'See our other stores'),
      '- Trade accounts available with tiered discounts',
      '- Free delivery on orders over £100',
      '- Same-day dispatch on orders before 2pm'
    ].join('\n');
  }

  function AdvisorWidget(container) {
    this.container = container;
    this.panel = container.querySelector('[data-advisor-panel]');
    this.trigger = container.querySelector('[data-advisor-trigger]');
    this.closeBtn = container.querySelector('[data-advisor-close]');
    this.messages = container.querySelector('[data-advisor-messages]');
    this.form = container.querySelector('[data-advisor-form]');
    this.input = container.querySelector('[data-advisor-input]');
    this.quickActions = container.querySelector('[data-advisor-quick-actions]');
    this.chatIcon = container.querySelector('.kt-advisor__trigger-icon--chat');
    this.closeIcon = container.querySelector('.kt-advisor__trigger-icon--close');

    this.apiHost = container.dataset.apiHost || '';
    this.customerId = container.dataset.customerId || '';
    this.customerName = container.dataset.customerName || '';
    this.isLoggedIn = container.dataset.loggedIn === 'true';
    this.store = container.dataset.store || 'eo';

    // Parse store config from data attribute (avoids hardcoding per store)
    try {
      this.storeConfig = JSON.parse(container.dataset.config || '{}');
    } catch (e) {
      this.storeConfig = {};
    }
    this.storeConfig.storeName = this.storeConfig.storeName || 'The Electrical Outlets';
    this.storeConfig.contactEmail = this.storeConfig.contactEmail || 'contact@electricalsoutlets.co.uk';
    this.storeConfig.domain = this.storeConfig.domain || 'theelectricaloutlets.com';
    this.storeConfig.sisterStores = this.storeConfig.sisterStores || '';

    this.conversationId = this.generateId();
    this.history = this.loadHistory();
    this.isOpen = false;
    this.isLoading = false;

    this.init();
  }

  AdvisorWidget.prototype.init = function() {
    const self = this;

    // Restore previous messages
    if (this.history.length > 0) {
      this.history.forEach(function(msg) {
        self.appendMessage(msg.role, msg.content, true);
      });
      this.hideQuickActions();
    }

    // Event listeners
    this.trigger.addEventListener('click', function() { self.toggle(); });
    this.closeBtn.addEventListener('click', function() { self.close(); });

    this.form.addEventListener('submit', function(e) {
      e.preventDefault();
      self.sendMessage();
    });

    // Quick action chips
    if (this.quickActions) {
      this.quickActions.addEventListener('click', function(e) {
        const chip = e.target.closest('[data-advisor-chip]');
        if (chip) {
          self.input.value = chip.dataset.advisorChip;
          self.sendMessage();
        }
      });
    }

    // Keyboard: Escape closes
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.isOpen) self.close();
    });

    // Click outside closes
    document.addEventListener('click', function(e) {
      if (self.isOpen && !self.container.contains(e.target)) {
        self.close();
      }
    });

    // Gather product context if on product page
    this.productContext = this.getProductContext();
  };

  AdvisorWidget.prototype.toggle = function() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  AdvisorWidget.prototype.open = function() {
    this.isOpen = true;
    this.panel.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.chatIcon.style.display = 'none';
    this.closeIcon.style.display = 'block';
    this.input.focus();
    this.scrollToBottom();
  };

  AdvisorWidget.prototype.close = function() {
    this.isOpen = false;
    this.panel.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.chatIcon.style.display = 'block';
    this.closeIcon.style.display = 'none';
  };

  AdvisorWidget.prototype.sendMessage = function() {
    const text = this.input.value.trim();
    if (!text || this.isLoading) return;

    this.input.value = '';
    this.hideQuickActions();
    this.appendMessage('user', text);
    this.addToHistory('user', text);
    this.showTyping();
    this.isLoading = true;

    const self = this;
    this.callAI(text)
      .then(function(response) {
        self.removeTyping();
        self.appendMessage('bot', response);
        self.addToHistory('assistant', response);
        self.isLoading = false;
      })
      .catch(function() {
        self.removeTyping();
        self.appendMessage('bot', 'Sorry, I had trouble processing that. Please try again or contact us at ' + self.storeConfig.contactEmail + ' for assistance.');
        self.isLoading = false;
      });
  };

  AdvisorWidget.prototype.callAI = function(message) {
    // Build messages array with system prompt and history
    const messages = [];

    // Add conversation history (last 10 exchanges)
    const recentHistory = this.history.slice(-10);
    recentHistory.forEach(function(msg) {
      messages.push({
        role: msg.role === 'bot' ? 'assistant' : msg.role,
        content: msg.content
      });
    });

    // Build the user message with product context
    let userMessage = message;
    if (this.productContext) {
      userMessage = '[Customer is viewing: ' + this.productContext + ']\n\n' + message;
    }
    if (this.customerName) {
      userMessage = '[Customer: ' + this.customerName + (this.isLoggedIn ? ' (logged in)' : '') + ']\n\n' + userMessage;
    }

    messages.push({ role: 'user', content: userMessage });

    // Call the AIMarketOps advisor endpoint. We intentionally do NOT pass
    // ?storeId= here — the AIMarketOps route reads the storeId via env
    // fallback (DEFAULT_STORE_ID), and the Phase 5 gate (phase5.ai-advisor)
    // keys on a CUID stored in the DB. Passing the slug ("eo") would
    // mismatch and force a permanent 503. Re-enable when AIMarketOps
    // adopts a slug→CUID alias map in
    // app/api/ai/product-advisor/route.ts (mirrors STORE_ALIASES from
    // scripts/enable-phase5-features.ts).
    const apiUrl = this.apiHost + '/api/ai/product-advisor';
    const body = {
      messages,
      systemPrompt: buildSystemPrompt(this.storeConfig),
      conversationId: this.conversationId,
      store: this.store,
      customerId: this.customerId
    };

    return fetchWithTimeout(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, API_TIMEOUT)
      .then(function(res) {
        if (!res.ok) throw new Error('API error ' + res.status);
        return res.json();
      })
      .then(function(data) {
        return data.response || data.message || 'I apologise, I could not generate a response. Please try again.';
      });
  };

  AdvisorWidget.prototype.appendMessage = function(role, content, skipScroll) {
    const msgDiv = document.createElement('div');
    msgDiv.className = 'kt-advisor__msg kt-advisor__msg--' + (role === 'user' ? 'user' : 'bot');

    const contentDiv = document.createElement('div');
    contentDiv.className = 'kt-advisor__msg-content';

    // Basic markdown-like formatting: **bold**, product links
    const formatted = this.formatMessage(content);
    contentDiv.innerHTML = formatted;

    msgDiv.appendChild(contentDiv);
    this.messages.appendChild(msgDiv);

    if (!skipScroll) this.scrollToBottom();
  };

  AdvisorWidget.prototype.formatMessage = function(text) {
    // Escape HTML first
    let escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Bold: **text**
    escaped = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Line breaks
    escaped = escaped.replace(/\n/g, '<br>');

    // Auto-link product URLs on our own domain only.
    //
    // Matched with a STATIC pattern plus an explicit host comparison, rather than
    // interpolating the domain into `new RegExp`. Building a pattern from config
    // is one bad settings value away from a catastrophic-backtracking regex run
    // against untrusted assistant output (and trips security/detect-non-literal-regexp).
    const linkDomain = this.storeConfig.domain || 'theelectricaloutlets.com';
    escaped = escaped.replace(/https?:\/\/[^\s<]+/g, function(url) {
      const afterScheme = url.replace(/^https?:\/\//, '');
      const slash = afterScheme.indexOf('/');
      // Require a path, matching the previous behaviour: a bare origin is not a
      // product link.
      if (slash < 1 || afterScheme.slice(0, slash) !== linkDomain) return url;
      return '<a href="' + url + '" target="_blank" rel="noopener" class="kt-advisor__product-card-link">View Product</a>';
    });

    return escaped;
  };

  AdvisorWidget.prototype.showTyping = function() {
    const typingDiv = document.createElement('div');
    typingDiv.className = 'kt-advisor__typing';
    typingDiv.setAttribute('data-advisor-typing', '');
    typingDiv.innerHTML = '<span class="kt-advisor__typing-dot"></span><span class="kt-advisor__typing-dot"></span><span class="kt-advisor__typing-dot"></span>';
    this.messages.appendChild(typingDiv);
    this.scrollToBottom();
  };

  AdvisorWidget.prototype.removeTyping = function() {
    const typing = this.messages.querySelector('[data-advisor-typing]');
    if (typing) typing.remove();
  };

  AdvisorWidget.prototype.hideQuickActions = function() {
    if (this.quickActions) this.quickActions.style.display = 'none';
  };

  AdvisorWidget.prototype.scrollToBottom = function() {
    const self = this;
    requestAnimationFrame(function() {
      self.messages.scrollTop = self.messages.scrollHeight;
    });
  };

  AdvisorWidget.prototype.getProductContext = function() {
    const contextEl = this.container.querySelector('[data-advisor-context] [data-product-title]');
    if (!contextEl) return null;

    const title = contextEl.dataset.productTitle || '';
    const type = contextEl.dataset.productType || '';
    const vendor = contextEl.dataset.productVendor || '';
    const price = contextEl.dataset.productPrice || '';
    const tags = contextEl.dataset.productTags || '';

    const parts = [title];
    if (vendor) parts.push('by ' + vendor);
    if (type) parts.push('(' + type + ')');
    if (price) parts.push('at ' + price);
    if (tags) {
      // Extract IP rating and compliance tags
      const tagList = tags.split(',');
      const relevantTags = tagList.filter(function(t) {
        return t.indexOf('ip') === 0 || t.indexOf('bs-') === 0 || t.indexOf('zone') === 0;
      });
      if (relevantTags.length > 0) parts.push('[' + relevantTags.join(', ') + ']');
    }

    return parts.join(' ');
  };

  // History management
  AdvisorWidget.prototype.addToHistory = function(role, content) {
    this.history.push({ role, content, timestamp: Date.now() });
    if (this.history.length > MAX_HISTORY) {
      this.history = this.history.slice(-MAX_HISTORY);
    }
    this.saveHistory();
  };

  AdvisorWidget.prototype.loadHistory = function() {
    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  };

  AdvisorWidget.prototype.saveHistory = function() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.history));
    } catch (e) {
      // Storage full or unavailable
    }
  };

  AdvisorWidget.prototype.generateId = function() {
    return 'adv_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  };

  // Fetch with timeout
  function fetchWithTimeout(url, options, timeout) {
    return new Promise(function(resolve, reject) {
      const controller = new AbortController();
      const timer = setTimeout(function() {
        controller.abort();
        reject(new Error('Request timed out'));
      }, timeout);

      fetch(url, Object.assign({}, options, { signal: controller.signal }))
        .then(function(res) {
          clearTimeout(timer);
          resolve(res);
        })
        .catch(function(err) {
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  // Initialize on DOM ready
  function init() {
    const container = document.getElementById('kt-product-advisor');
    // Keep the instance on the element rather than discarding it (`new` purely
    // for side effects trips no-new). It also makes a double-init detectable and
    // gives the console a handle for debugging.
    if (container && !container.ktAdvisor) {
      container.ktAdvisor = new AdvisorWidget(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
